// Sound lessons: short lessons for one hard sound (Leanne, 25 Oct 2026),
// opened from "Sound to work on". Hear it, make it, hear the difference, say
// it and hear yourself back (no score: a word scored on its own was dropped,
// see WordVoiceButton), then the matching drill collection, which is scored.
//
// Which sounds a lesson covers is set on the server (FRENCH_PHONES lesson column).
// Phonetics follow PHONETIC-KEY.md: u = ew, ou = oo.
export const SOUND_LESSONS = {
    'ou-u': {
        id: 'ou-u',
        titlePrefix: 'Pronunciation lesson:',
        versus: ['ou', 'u'],             // shown "ou vs u", the vs in green
        name: 'ou vs u',                 // short, for "Learn this sound: ou vs u"
        practiseLabel: 'Practise the ou and u sounds in phrases',
        drillTopic: 'ou_u',
        // Why it is hard for English speakers, and the usual mistakes (Leanne,
        // 26 Oct 2026: the lesson should say so).
        whyHard: 'French has two different sounds here, u (as in tu) and ou (as in tout), and English has neither exactly. To an English ear they both sound like "oo", but to a French ear they are different words: tu is "you", tout is "all", dessus is "on top" and dessous is "underneath". Mix them up and you say something else.',
        mistakes: [
            'Saying u as "oo": tu becomes tout.',
            'Saying u as "you", with a slide: keep it one pure sound.',
            'Spreading your lips for u: keep them tightly rounded.',
            'Saying ou like the English "oo" in "too": in British English that sits halfway between the two French sounds. Pull your tongue further back.',
            'Starting huit with ou: it starts from u, so huit and oui, lui and Louis, sound different.',
        ],
        hear: [
            { label: 'ou', words: ['tout', 'vous', 'roue', 'dessous', 'doux'] },
            { label: 'u', words: ['tu', 'vu', 'rue', 'dessus', 'du'] },
            // The u gliding into the next vowel (huit), and the pairs it is
            // mixed up with (Leanne, 27 Oct 2026).
            { label: 'ui', heading: 'Sound: u gliding, as in huit', words: ['huit', 'lui', 'nuit', 'puis', 'suis'] },
            { label: 'oui-huit', heading: 'Compare: oui / huit, Louis / lui', words: ['oui', 'huit', 'Louis', 'lui'] },
        ],
        make: [
            { label: 'ou', text: 'Round your lips, tongue pulled back.' },
            { label: 'u', text: 'Say "ee", keep your tongue there, round your lips.' },
            { label: 'u in huit', text: 'Start the u of tu and slide straight into the next vowel.' },
        ],
        // ou on the left, u on the right, the same order as the lists above.
        pairLabels: ['ou', 'u'],
        pairs: [['tout', 'tu'], ['vous', 'vu'], ['roue', 'rue'], ['dessous', 'dessus'], ['doux', 'du']],
        phonetics: {
            english_syllables: {
                tu: 'tew', tout: 'too', vu: 'vew', vous: 'voo', rue: 'rew', roue: 'roo',
                dessus: 'duh-SEW', dessous: 'duh-SOO', du: 'dew', doux: 'doo',
                huit: 'weet', lui: 'lwee', nuit: 'nwee', puis: 'pwee', suis: 'swee', oui: 'wee', Louis: 'lwee',
            },
            ipa: {
                tu: '/ty/', tout: '/tu/', vu: '/vy/', vous: '/vu/', rue: '/ʁy/', roue: '/ʁu/',
                dessus: '/dəsy/', dessous: '/dəsu/', du: '/dy/', doux: '/du/',
                huit: '/ɥit/', lui: '/lɥi/', nuit: '/nɥi/', puis: '/pɥi/', suis: '/sɥi/', oui: '/wi/', Louis: '/lwi/',
            },
        },
    },
    r: {
        id: 'r',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'the French r',
        name: 'the French r',
        practiseLabel: 'Practise the French r in phrases',
        drillTopic: 'french_r',
        whyHard: 'The French r is made at the back of the throat. The English r uses the tip of the tongue, and British English often drops the r at the end of a word. The French r also changes a little with its place in the word: clear at the start (rue), almost whispered after t, p, k or f (très, crois), and light but still there at the end (bonjour).',
        mistakes: [
            'Using the English r, with the tongue tip curled up.',
            'Dropping the final r: bonjour becomes "bonjou".',
            'Rolling it, as in Spanish or Italian.',
            'Making it too harsh: it is a light gargle, not clearing your throat.',
        ],
        hear: [
            { label: 'r at the start', heading: 'At the start', words: ['rue', 'rouge', 'rose', 'riz', 'rire'] },
            { label: 'r after a consonant', heading: 'After a consonant', words: ['très', 'gros', 'frère', 'prendre', 'crois'] },
            { label: 'r at the end', heading: 'At the end', words: ['bonjour', 'mer', 'pour', 'sur', 'cher'] },
        ],
        make: [
            { label: 'Tongue', text: 'Tip down behind your bottom teeth. Lift the back of your tongue, like a light gargle.' },
            { label: 'Tip', text: 'Start from a soft g and let the air through.' },
        ],
        // Without r on the left, with r on the right: the final r British
        // English drops ("car" said "cah").
        pairLabels: ['no r', 'r'],
        pairs: [['pas', 'par'], ['fou', 'four'], ['sous', 'sourd'], ['bas', 'bar'], ['peu', 'peur']],
        phonetics: {
            english_syllables: {
                rue: 'rew', rouge: 'roozh', rose: 'rohz', riz: 'ree', rire: 'reer',
                'très': 'treh', gros: 'groh', 'frère': 'frehr', prendre: 'prahndr', crois: 'krwah',
                bonjour: 'bohn-ZHOOR', mer: 'mehr', pour: 'poor', sur: 'sewr', cher: 'shehr',
                pas: 'pah', par: 'pahr', fou: 'foo', four: 'foor', sous: 'soo', sourd: 'soor',
                bas: 'bah', bar: 'bahr', peu: 'puh', peur: 'puhr',
            },
            ipa: {
                rue: '/ʁy/', rouge: '/ʁuʒ/', rose: '/ʁoz/', riz: '/ʁi/', rire: '/ʁiʁ/',
                'très': '/tʁɛ/', gros: '/ɡʁo/', 'frère': '/fʁɛʁ/', prendre: '/pʁɑ̃dʁ/', crois: '/kʁwa/',
                bonjour: '/bɔ̃ʒuʁ/', mer: '/mɛʁ/', pour: '/puʁ/', sur: '/syʁ/', cher: '/ʃɛʁ/',
                pas: '/pa/', par: '/paʁ/', fou: '/fu/', four: '/fuʁ/', sous: '/su/', sourd: '/suʁ/',
                bas: '/ba/', bar: '/baʁ/', peu: '/pø/', peur: '/pœʁ/',
            },
        },
    },
    nasals: {
        id: 'nasals',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'an, on, in',
        name: 'an, on, in',
        practiseLabel: 'Practise the nasal sounds in phrases',
        drillTopic: 'nasals',
        whyHard: 'English has no nasal vowels. In French, an, on and in are said with air going out through your nose and mouth at the same time, and the n is not pronounced. English speakers tend to say a vowel followed by an n, and to mix the three up, which changes the word: vent (wind), vont (they go), vin (wine). The fourth nasal, un, is now said the same as in by most French speakers, so brun sounds like brin.',
        mistakes: [
            'Saying the n at the end: bon comes out as "bonn".',
            'Mixing up the three: vent turns into vont or vin.',
            'Closing your mouth to finish the sound: keep it open to the end.',
            'Saying on like the English "on" in "on time": the French on is rounded, closer to "oh".',
            'Saying in like the English "in": the French in is more open, like è with air through your nose.',
        ],
        hear: [
            { label: 'an', words: ['banc', 'vent', 'sans', 'lent', 'temps'] },
            { label: 'on', words: ['bon', 'vont', 'son', 'long', 'ton'] },
            { label: 'in', words: ['bain', 'vin', 'saint', 'lin', 'teint'] },
        ],
        make: [
            { label: 'an', text: 'Mouth open as for "ah", and let the air out through your nose too. No n.' },
            { label: 'on', text: 'Lips rounded in a small circle, as for "oh", air through your nose too. No n.' },
            { label: 'in', text: 'Mouth as for è (as in "bed"), lips relaxed, air through your nose too. No n.' },
        ],
        // Sets of three, an / on / in, the same order as the lists above.
        pairLabels: ['an', 'on', 'in'],
        pairs: [['banc', 'bon', 'bain'], ['vent', 'vont', 'vin'], ['sans', 'son', 'saint'],
                ['lent', 'long', 'lin'], ['temps', 'ton', 'teint']],
        phonetics: {
            english_syllables: {
                banc: 'bahn', vent: 'vahn', sans: 'sahn', lent: 'lahn', temps: 'tahn',
                bon: 'bohn', vont: 'vohn', son: 'sohn', long: 'lohn', ton: 'tohn',
                bain: 'ban', vin: 'van', saint: 'san', lin: 'lan', teint: 'tan',
            },
            ipa: {
                banc: '/bɑ̃/', vent: '/vɑ̃/', sans: '/sɑ̃/', lent: '/lɑ̃/', temps: '/tɑ̃/',
                bon: '/bɔ̃/', vont: '/vɔ̃/', son: '/sɔ̃/', long: '/lɔ̃/', ton: '/tɔ̃/',
                bain: '/bɛ̃/', vin: '/vɛ̃/', saint: '/sɛ̃/', lin: '/lɛ̃/', teint: '/tɛ̃/',
            },
        },
    },
    'i-e-e': {
        id: 'i-e-e',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'i, é, è',
        name: 'i, é, è',
        practiseLabel: 'Practise the vowels i, é and è in phrases',
        drillTopic: 'pure_vowels',
        whyHard: 'French has three vowels that sit close together: i (si), é (café) and è (très). Each one changes the word: lit (bed), les (the), lait (milk). English speakers tend to let them slide, so café ends with an "ee" sound, "caf-ay-ee". In French each vowel stays the same from start to finish.',
        mistakes: [
            'Sliding é into "ee": café becomes "caf-ay-ee".',
            'Shortening i: ville sounds like the English "vill".',
            'Saying é and è the same: les (the) and lait (milk) sound alike.',
        ],
        hear: [
            { label: 'i', words: ['si', 'lit', 'midi', 'ville', 'fille'] },
            { label: 'é', words: ['café', 'été', 'thé', 'nez', 'parler'] },
            { label: 'è', words: ['très', 'mère', 'fête', 'lait', 'mais'] },
        ],
        make: [
            { label: 'i', text: 'Smile, tongue high. Short and still.' },
            { label: 'é', text: 'Smile a little less, mouth slightly more open. Hold it still, no "ee" at the end.' },
            { label: 'è', text: 'Mouth more open, as in the English "bed". Hold it flat.' },
        ],
        // Sets of three, i / é / è, the same order as the lists above.
        pairLabels: ['i', 'é', 'è'],
        pairs: [['lit', 'les', 'lait'], ['si', 'ses', 'sait'], ['fit', 'fée', 'fait'], ['pris', 'pré', 'prêt']],
        phonetics: {
            english_syllables: {
                si: 'see', lit: 'lee', midi: 'mee-DEE', ville: 'veel', fille: 'feey',
                'café': 'kah-FAY', 'été': 'ay-TAY', 'thé': 'tay', nez: 'nay', parler: 'pahr-LAY',
                'très': 'treh', 'mère': 'mehr', 'fête': 'feht', lait: 'leh', mais: 'meh',
                les: 'lay', ses: 'say', sait: 'seh', fit: 'fee', 'fée': 'fay', fait: 'feh',
                pris: 'pree', 'pré': 'pray', 'prêt': 'preh',
            },
            ipa: {
                si: '/si/', lit: '/li/', midi: '/midi/', ville: '/vil/', fille: '/fij/',
                'café': '/kafe/', 'été': '/ete/', 'thé': '/te/', nez: '/ne/', parler: '/paʁle/',
                'très': '/tʁɛ/', 'mère': '/mɛʁ/', 'fête': '/fɛt/', lait: '/lɛ/', mais: '/mɛ/',
                les: '/le/', ses: '/se/', sait: '/sɛ/', fit: '/fi/', 'fée': '/fe/', fait: '/fɛ/',
                pris: '/pʁi/', 'pré': '/pʁe/', 'prêt': '/pʁɛ/',
            },
        },
    },
    // The four below written 27 Oct 2026, from the approved sound table
    // (SOUND-TABLE-DRAFT.md). Ids match the lesson column of FRENCH_PHONES.
    eu: {
        id: 'eu',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'eu, œu and the e of le',
        name: 'eu, œu, e',
        practiseLabel: 'Practise eu, œu and the e of le in phrases',
        drillTopic: 'eu_e',
        whyHard: 'French has three sounds here that English does not: the closed eu of peu, the open œu of peur, and the short, light e of le. The first two are made with rounded lips, which English speakers tend to forget, so they come out flat, like the "er" of "her". The e of le is easy to say as "lay", which turns le (the, one thing) into les (the, more than one).',
        mistakes: [
            'Flat lips for eu: peu comes out like the English "puh". Round your lips.',
            'Saying le like les ("lay"): le livre becomes les livres.',
            'Making the e of le long or stressed: it is short and light, and often dropped (je suis sounds like "j\'suis").',
            'Using the same sound for peu and peur: peu is closed, peur opens up before the r.',
        ],
        hear: [
            { label: 'eu', words: ['peu', 'deux', 'bleu', 'jeu', 'feu'] },
            { label: 'œu', words: ['peur', 'sœur', 'fleur', 'heure', 'neuf'] },
            { label: 'e', heading: 'Sound: the e of le', words: ['le', 'je', 'de', 'ce', 'me'] },
        ],
        make: [
            { label: 'eu', text: 'Say é, keep your tongue still, then round your lips.' },
            { label: 'œu', text: 'Say è, keep your tongue still, then round your lips. Mouth a little more open than for eu.' },
            { label: 'e', text: 'Short and relaxed, lips slightly rounded. Never "lay".' },
        ],
        // Closed eu on the left, open œu on the right.
        pairLabels: ['eu', 'œu'],
        pairs: [['peu', 'peur'], ['ceux', 'sœur'], ['il veut', 'ils veulent'], ['il peut', 'ils peuvent'], ['bœufs', 'bœuf']],
        phonetics: {
            english_syllables: {
                peu: 'puh', deux: 'duh', bleu: 'bluh', jeu: 'zhuh', feu: 'fuh',
                peur: 'puhr', 'sœur': 'suhr', fleur: 'fluhr', heure: 'uhr', neuf: 'nuhf',
                le: 'luh', je: 'zhuh', de: 'duh', ce: 'suh', me: 'muh',
                ceux: 'suh', 'il veut': 'eel vuh', 'ils veulent': 'eel vuhl',
                'il peut': 'eel puh', 'ils peuvent': 'eel puhv', 'bœufs': 'buh', 'bœuf': 'buhf',
            },
            ipa: {
                peu: '/pø/', deux: '/dø/', bleu: '/blø/', jeu: '/ʒø/', feu: '/fø/',
                peur: '/pœʁ/', 'sœur': '/sœʁ/', fleur: '/flœʁ/', heure: '/œʁ/', neuf: '/nœf/',
                le: '/lə/', je: '/ʒə/', de: '/də/', ce: '/sə/', me: '/mə/',
                ceux: '/sø/', 'il veut': '/il vø/', 'ils veulent': '/il vœl/',
                'il peut': '/il pø/', 'ils peuvent': '/il pœv/', 'bœufs': '/bø/', 'bœuf': '/bœf/',
            },
        },
    },
    'a-o': {
        id: 'a-o',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'pure a and o',
        name: 'a and o',
        practiseLabel: 'Practise a, eau and o in phrases',
        drillTopic: 'a_o',
        whyHard: 'English vowels glide: "go" ends with a w sound and "day" with a y. French a, eau and o are pure: the mouth stays still from start to finish. French also has two o sounds, the closed eau of beau and the open o of porte, and swapping them can change the word: paume (palm) and pomme (apple).',
        mistakes: [
            'Adding a w after eau: beau sounds like the English "bow".',
            'Saying a like the a in "cat": chat should be open, like the a in "father" but short.',
            'Using the same o for paume and pomme: pomme is more open.',
            'Saying the o of porte with flat lips, as in the English "pot": keep them rounded.',
        ],
        hear: [
            { label: 'a', words: ['chat', 'la', 'ça', 'papa', 'Paris'] },
            { label: 'eau', words: ['beau', 'eau', 'mot', 'chaud', 'rose'] },
            { label: 'o', words: ['porte', 'bonne', 'homme', 'robe', 'sol'] },
        ],
        make: [
            { label: 'a', text: 'Mouth open, tongue low. Short and still.' },
            { label: 'eau', text: 'Lips rounded in a small circle. Hold it still, no w at the end.' },
            { label: 'o', text: 'Mouth more open than for eau, lips still rounded.' },
        ],
        // Closed eau on the left, open o on the right.
        pairLabels: ['eau', 'o'],
        pairs: [['paume', 'pomme'], ['saule', 'sol'], ['nôtre', 'notre'], ['vôtre', 'votre']],
        phonetics: {
            english_syllables: {
                chat: 'shah', la: 'lah', 'ça': 'sah', papa: 'pah-PAH', Paris: 'pah-REE',
                beau: 'boh', eau: 'oh', mot: 'moh', chaud: 'shoh', rose: 'rohz',
                porte: 'port', bonne: 'bon', homme: 'om', robe: 'rob', sol: 'sol',
                paume: 'pohm', pomme: 'pom', saule: 'sohl', 'nôtre': 'nohtr', notre: 'notr',
                'vôtre': 'vohtr', votre: 'votr',
            },
            ipa: {
                chat: '/ʃa/', la: '/la/', 'ça': '/sa/', papa: '/papa/', Paris: '/paʁi/',
                beau: '/bo/', eau: '/o/', mot: '/mo/', chaud: '/ʃo/', rose: '/ʁoz/',
                porte: '/pɔʁt/', bonne: '/bɔn/', homme: '/ɔm/', robe: '/ʁɔb/', sol: '/sɔl/',
                paume: '/pom/', pomme: '/pɔm/', saule: '/sol/', 'nôtre': '/notʁ/', notre: '/nɔtʁ/',
                'vôtre': '/votʁ/', votre: '/vɔtʁ/',
            },
        },
    },
    consonants: {
        id: 'consonants',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'crisp consonants',
        name: 'crisp consonants',
        practiseLabel: 'Practise crisp consonants in phrases',
        drillTopic: 'consonants',
        whyHard: 'In English, p, t and k come with a little puff of air: hold your hand in front of your mouth and say "pin". French has no puff, so these sounds are sharper. French t, d, n and l are also made with the tongue against the back of the top teeth, not the ridge behind them, and the French l stays light even at the end of a word, where English makes it dark ("full"). Small differences, but together they are a big part of an English accent.',
        mistakes: [
            'A puff of air after p, t or k: papa sounds English.',
            'Tongue on the ridge behind the teeth for t and d: they sound heavy.',
            'A dark l at the end of a word: belle sounds like the English "bell".',
            'Softening the t between two vowels, American style: petit keeps a clear t.',
        ],
        hear: [
            { label: 'p t k', heading: 'p, t, k: no puff of air', words: ['papa', 'tout', 'café', 'petit', 'quatre'] },
            { label: 'd n l', heading: 'd, n, l: tongue on the teeth', words: ['dire', 'non', 'nous', 'lit', 'lune'] },
            { label: 'l at the end', heading: 'A light l at the end', words: ['belle', 'ville', 'sel', 'mille', 'bol'] },
        ],
        make: [
            { label: 'p, t, k', text: 'No puff of air. A tissue held in front of your mouth should not move.' },
            { label: 't, d, n, l', text: 'Tongue tip against the back of your top teeth.' },
            { label: 'l', text: 'Light at the end of a word too, tongue on the teeth: belle, not "bell".' },
        ],
        // Without the puff, p t k can sound like b d g to an English ear, so
        // the comparison is with them.
        pairLabels: ['p t k', 'b d g'],
        pairs: [['pain', 'bain'], ['tout', 'doux'], ['quand', 'gant'], ['pont', 'bon'], ['temps', 'dans']],
        phonetics: {
            english_syllables: {
                papa: 'pah-PAH', tout: 'too', 'café': 'kah-FAY', petit: 'puh-TEE', quatre: 'kahtr',
                dire: 'deer', non: 'nohn', nous: 'noo', lit: 'lee', lune: 'lewn',
                belle: 'bel', ville: 'veel', sel: 'sel', mille: 'meel', bol: 'bol',
                pain: 'pan', bain: 'ban', doux: 'doo', quand: 'kahn', gant: 'gahn',
                pont: 'pohn', bon: 'bohn', temps: 'tahn', dans: 'dahn',
            },
            ipa: {
                papa: '/papa/', tout: '/tu/', 'café': '/kafe/', petit: '/pəti/', quatre: '/katʁ/',
                dire: '/diʁ/', non: '/nɔ̃/', nous: '/nu/', lit: '/li/', lune: '/lyn/',
                belle: '/bɛl/', ville: '/vil/', sel: '/sɛl/', mille: '/mil/', bol: '/bɔl/',
                pain: '/pɛ̃/', bain: '/bɛ̃/', doux: '/du/', quand: '/kɑ̃/', gant: '/ɡɑ̃/',
                pont: '/pɔ̃/', bon: '/bɔ̃/', temps: '/tɑ̃/', dans: '/dɑ̃/',
            },
        },
    },
    'j-ch-gn': {
        id: 'j-ch-gn',
        titlePrefix: 'Pronunciation lesson:',
        titleMain: 'j, ch, gn',
        name: 'j, ch, gn',
        practiseLabel: 'Practise j, ch and gn in phrases',
        drillTopic: 'j_ch_gn',
        whyHard: 'English has the sound of the French j only in the middle of words like "measure", never at the start, so English speakers say "dj" as in "jam": je becomes "djuh". In French, ch is always "sh", never the "tch" of "church". And gn is one sound, like the ny in "canyon", which English speakers tend to split into n and g: montagne said "mon-tag-nuh".',
        mistakes: [
            'Saying j as "dj": je becomes "djuh".',
            'Saying ch as "tch": chat becomes "tchah".',
            'Splitting gn into n and g: montagne becomes "mon-tag-nuh".',
            'Mixing up j and ch: bouger (to move) becomes boucher (butcher).',
        ],
        hear: [
            { label: 'j', words: ['je', 'jour', 'rouge', 'jardin', 'manger'] },
            { label: 'ch', words: ['chat', 'chose', 'cher', 'riche', 'chaud'] },
            { label: 'gn', words: ['montagne', 'gagner', 'ligne', 'Espagne', 'champignon'] },
        ],
        make: [
            { label: 'j', text: 'Like the s in "measure", lips pushed forward a little. No d before it.' },
            { label: 'ch', text: 'Like the sh in "shoe", lips pushed forward a little.' },
            { label: 'gn', text: 'Like the ny in "canyon": one sound, the middle of your tongue flat against the roof of your mouth.' },
        ],
        pairLabels: ['j', 'ch'],
        pairs: [['joue', 'chou'], ["j'ai", 'chez'], ['gêne', 'chaîne'], ['bouger', 'boucher'], ['gens', 'chant']],
        phonetics: {
            english_syllables: {
                je: 'zhuh', jour: 'zhoor', rouge: 'roozh', jardin: 'zhahr-DAN', manger: 'mahn-ZHAY',
                chat: 'shah', chose: 'shohz', cher: 'shehr', riche: 'reesh', chaud: 'shoh',
                montagne: 'mohn-TAHNY', gagner: 'gah-NYAY', ligne: 'leeny', Espagne: 'es-PAHNY',
                champignon: 'shahn-pee-NYOHN',
                joue: 'zhoo', chou: 'shoo', "j'ai": 'zhay', chez: 'shay', 'gêne': 'zhen', 'chaîne': 'shen',
                bouger: 'boo-ZHAY', boucher: 'boo-SHAY', gens: 'zhahn', chant: 'shahn',
            },
            ipa: {
                je: '/ʒə/', jour: '/ʒuʁ/', rouge: '/ʁuʒ/', jardin: '/ʒaʁdɛ̃/', manger: '/mɑ̃ʒe/',
                chat: '/ʃa/', chose: '/ʃoz/', cher: '/ʃɛʁ/', riche: '/ʁiʃ/', chaud: '/ʃo/',
                montagne: '/mɔ̃taɲ/', gagner: '/ɡaɲe/', ligne: '/liɲ/', Espagne: '/ɛspaɲ/',
                champignon: '/ʃɑ̃piɲɔ̃/',
                joue: '/ʒu/', chou: '/ʃu/', "j'ai": '/ʒe/', chez: '/ʃe/', 'gêne': '/ʒɛn/', 'chaîne': '/ʃɛn/',
                bouger: '/buʒe/', boucher: '/buʃe/', gens: '/ʒɑ̃/', chant: '/ʃɑ̃/',
            },
        },
    },
};

// The test accounts only while the pilot is checked (/auth/me sound_lessons).
let soundLessonsOn = false;
export const setSoundLessons = (on) => { soundLessonsOn = !!on; };
export const soundLessonsEnabled = () => soundLessonsOn;

// Which lesson covers a sound is decided on the server, in one table with the
// sound's name and tip (FRENCH_PHONES), and sent as the lesson id. This only
// says whether that lesson exists here yet and is switched on.
export const lessonById = (id) => (soundLessonsOn && id ? SOUND_LESSONS[id] || null : null);

// Phrases practised from a lesson without being added are given "lesson-"
// ids by the server, so the practice screen knows not to save them.
export const isLessonPhrase = (phrase) => String(phrase?.id || '').startsWith('lesson-');
